// Dashboard.js
import React, { useState, useEffect } from "react";
import {
  FaChartBar,
  FaEnvelope,
  FaUserCheck,
  FaCalendarCheck,
  FaChartLine,
} from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";

console.log("Dashboard.js file executing - TOP OF FILE");
console.log("Dashboard.js loaded");

// HARD-CODED DUMMY DATA - ALWAYS USE THIS DATA
const dummyCampaigns = [
  {
    id: "dummy-1",
    name: "Waitlist registration campaign",
    emailsSent: 320,
    engagedLeads: 16,
    meetingsBooked: 1,
    createdAt: new Date(),
  },
  {
    id: "dummy-2",
    name: "LinkedIn personalization test campaign",
    emailsSent: 543,
    engagedLeads: 111,
    meetingsBooked: 2,
    createdAt: new Date(),
  },
  {
    id: "dummy-3",
    name: "Enterprise Cold Outreach",
    emailsSent: 200,
    engagedLeads: 87,
    meetingsBooked: 0,
    createdAt: new Date(),
  },
];

// HARD-CODED DUMMY STATS - ALWAYS USE THESE STATS
const dummyStats = {
  totalEmailsSent: 1063,
  engagedLeads: 325,
  totalMeetingsBooked: 3,
  responseRate: 4.62,
  avgEmailsPerDay: 12,
};

// HARD-CODED DUMMY TOP CAMPAIGNS - ALWAYS USE THESE
const dummyTopCampaigns = [
  {
    name: "Waitlist registration campaign",
    persona: "Founder - waitlist follow-up persona",
    engagedLeads: 16,
    responseRate: 18.75,
    bounceRate: 2.5,
    openRate: 72.3,
  },
  {
    name: "LinkedIn personalization test campaign",
    persona: "Founder persona (outbound, short)",
    engagedLeads: 111,
    responseRate: 8.11,
    bounceRate: 1.2,
    openRate: 68.5,
  },
  {
    name: "Enterprise Cold Outreach",
    persona: "Alex - Senior Consultant",
    engagedLeads: 87,
    responseRate: 6.32,
    bounceRate: 3.1,
    openRate: 54.8,
  },
];

const Dashboard = () => {
  console.log("Dashboard COMPONENT FUNCTION EXECUTING");

  // Initialize with dummy data ALWAYS - no exceptions, no loading states
  const [campaigns] = useState(dummyCampaigns);
  const [selectedPeriod, setSelectedPeriod] = useState("Last 90 days");
  const [selectedCampaign, setSelectedCampaign] = useState("All campaigns");
  const [stats] = useState(dummyStats);
  const [topPerformingCampaigns] = useState(dummyTopCampaigns);

  useEffect(() => {
    // Show notification that sample data is being used
    toast.info("Sample Data", {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, []);

  console.log("Dashboard component initialized with dummy data");
  console.log(
    "Ready to render with:",
    campaigns.length,
    "campaigns, stats:",
    stats.totalEmailsSent,
    "emails sent"
  );

  return (
    <div className="flex min-h-screen bg-gray-50">
      <ToastContainer />
      <div className="flex-1 p-8">
        <h1 className="text-2xl font-bold mb-6">Campaign Dashboard</h1>

        {/* Filter controls */}
        <div className="flex flex-col md:flex-row justify-between items-start mb-8">
          <div className="mb-4 md:mb-0">
            <label className="block text-sm text-gray-600 mb-1">
              Campaign activity
            </label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="w-64 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option>All campaigns</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-1">Period</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-48 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              {["Last 7 days", "Last 30 days", "Last 90 days", "Last year"].map(
                (period) => (
                  <option key={period} value={period}>
                    {period}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {/* Performance Overview section */}
        <h2 className="text-xl font-semibold mb-4">Performance Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Emails Sent Card */}
          <div className="bg-blue-400 text-white rounded-lg shadow-md p-6">
            <h3 className="uppercase text-xs font-semibold mb-4 tracking-wide">
              Total Emails Sent
            </h3>
            <div className="text-4xl font-bold">
              {stats.totalEmailsSent.toLocaleString()}
            </div>
          </div>

          {/* Engaged Leads Card */}
          <div className="bg-blue-400 text-white rounded-lg shadow-md p-6">
            <h3 className="uppercase text-xs font-semibold mb-4 tracking-wide">
              Engaged Leads
            </h3>
            <div className="text-4xl font-bold">
              {stats.engagedLeads.toLocaleString()}
            </div>
          </div>

          {/* Total Meetings Booked Card */}
          <div className="bg-blue-400 text-white rounded-lg shadow-md p-6">
            <h3 className="uppercase text-xs font-semibold mb-4 tracking-wide">
              Total Meetings Booked
            </h3>
            <div className="text-4xl font-bold">
              {stats.totalMeetingsBooked}
            </div>
          </div>

          {/* Response Rate Card */}
          <div className="bg-blue-400 text-white rounded-lg shadow-md p-6">
            <h3 className="uppercase text-xs font-semibold mb-4 tracking-wide">
              Response Rate
            </h3>
            <div className="text-4xl font-bold">{stats.responseRate}%</div>
          </div>
        </div>

        {/* Sending Volume and Top Performing Campaigns section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sending Volume per Day */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">
              Sending Volume per Day
            </h3>
            <div className="h-64 flex items-center justify-center">
              <div className="relative">
                <div className="w-32 h-32 rounded-full border-8 border-blue-400 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold">
                      {stats.avgEmailsPerDay}
                    </div>
                    <div className="text-xs text-gray-500">
                      AVG emails per day
                    </div>
                  </div>
                </div>
                {/* This would be replaced with an actual chart */}
                <div className="text-center mt-4 text-sm text-gray-500"></div>
              </div>
            </div>
          </div>

          {/* Top Performing Campaigns */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">
              Top Performing Campaigns
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-blue-400 text-white">
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Persona</th>
                    <th className="px-4 py-2 text-center">Engaged Leads</th>
                    <th className="px-4 py-2 text-center">Response Rate</th>
                    <th className="px-4 py-2 text-center">Bounce Rate</th>
                    <th className="px-4 py-2 text-center">Open Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformingCampaigns.map((campaign, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                    >
                      <td className="px-4 py-3 text-sm">{campaign.name}</td>
                      <td className="px-4 py-3 text-sm">{campaign.persona}</td>
                      <td className="px-4 py-3 text-sm text-center">
                        {campaign.engagedLeads}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {campaign.responseRate}%
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {campaign.bounceRate || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        {campaign.openRate || "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

console.log("About to export Dashboard component");
export default Dashboard;
